import { useRef } from 'react';

interface LightBoxProps {
  images: string[];
  activeImage: string;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  alt?: string;
}

const SWIPE_THRESHOLD_PX = 50;

export default function LightBox({
  images,
  activeImage,
  onClose,
  onPrev,
  onNext,
  alt = 'Selected Product',
}: LightBoxProps) {
  const touchStartXRef = useRef<number | null>(null);

  return (
    <div
      className="fixed inset-0 z-[500] bg-white/90 flex
      items-center justify-center p-[20px]"
      onClick={onClose}
      onTouchStart={(e) => {
        touchStartXRef.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartXRef.current === null) return;
        const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
        touchStartXRef.current = null;
        if (deltaX > SWIPE_THRESHOLD_PX) onPrev();
        else if (deltaX < -SWIPE_THRESHOLD_PX) onNext();
      }}
    >
      <button
        onClick={onClose}
        type="button"
        className="absolute top-1.5 right-1.5 p-1 rounded-full
            text-gray-300 hover:text-red-400 
            bg-primary-50 
            border-[2px]
            border-solid
            border-secondary-300
            text-primary-500
            hover:bg-red-50
            transition-colors"
        aria-label="Close lightbox"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      {images.length > 1 && (
        <>
          <button
            type="button"
            title="Previous image"
            aria-label="Previous image"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            className="p-0 absolute left-[10px] md:left-[20px] top-1/2 -translate-y-1/2
            w-[36px] h-[36px] rounded-full bg-white/90 hover:bg-white flex
            items-center justify-center text-black shadow-md cursor-pointer"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-[16px] h-[16px]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            type="button"
            title="Next image"
            aria-label="Next image"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="p-0 absolute right-[10px] md:right-[20px] top-1/2 -translate-y-1/2
            w-[36px] h-[36px] rounded-full bg-white/90 hover:bg-white flex
            items-center justify-center text-black shadow-md cursor-pointer"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-[16px] h-[16px]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </>
      )}

      <img
        src={activeImage}
        className="max-w-full max-h-full object-contain rounded-md"
        alt={alt}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}
